import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { List, Icon, Loader, Divider } from 'semantic-ui-react';
import {
  PageSection,
  Subtitle,
  StyledCard,
  H3,
  Caption,
  ControlContainerRow,
  ControlContainer,
} from 'common/styles/page';
import { getFileTypeIcon } from 'common/constants/filetypes';
import { StatusIcon } from 'common/components/StatusIcon';
import DoughnutGauge from 'areas/dashboard/components/DoughnutGauge';
import { useApiAdminV1RepositoryRepositoryFileShareListByOrganizationRetrieveQuery } from 'store/generated/admin/repository-file-share';
import { getCurrentOrganizationId } from 'common/selectors/auth';

const FileTypes = { PDF: 0, MP4: 1, LINK: 2 };

interface Props {
  selectedEmployeeId?: string | null;
  onSelectFile?: (filename: string) => void;
}

const AnalyticsResourceMember: React.FC<Props> = ({ selectedEmployeeId, onSelectFile }) => {
  const organizationId = useSelector(getCurrentOrganizationId);

  const { data: resourceData, isLoading } =
    useApiAdminV1RepositoryRepositoryFileShareListByOrganizationRetrieveQuery({ organizationId });

  const [sortKey, setSortKey] = useState<'employee_name' | 'option_name'>('employee_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);

  const groupedByEmployee = useMemo(() => {
    if (!Array.isArray(resourceData)) return {};
    return resourceData.reduce((acc: Record<string, any[]>, item) => {
      const id = item.employee_id ?? 'unknown';
      if (!acc[id]) acc[id] = [];
      acc[id].push(item);
      return acc;
    }, {});
  }, [resourceData]);

  const employees = useMemo(
    () =>
      Object.entries(groupedByEmployee)
        .map(([id, items]) => items[0])
        .filter(Boolean),
    [groupedByEmployee]
  );

  const sortedEmployees = useMemo(
    () =>
      [...employees].sort((a, b) => {
        const aVal = a[sortKey] ?? '';
        const bVal = b[sortKey] ?? '';
        return sortDirection === 'asc'
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      }),
    [employees, sortKey, sortDirection]
  );

  const categorizedFiles = useMemo(() => {
    const result: Record<number, any[]> = {
      [FileTypes.PDF]: [],
      [FileTypes.MP4]: [],
      [FileTypes.LINK]: [],
    };
    if (selectedEmployee) {
      groupedByEmployee[selectedEmployee.employee_id].forEach(file => {
        result[file.file_type].push(file);
      });
    }
    return result;
  }, [selectedEmployee, groupedByEmployee]);

  const percentAccessed = useMemo(() => {
    if (!selectedEmployee) return 0;
    const files = groupedByEmployee[selectedEmployee.employee_id];
    const accessed = files.filter(f => f.access_count > 0).length;
    return (accessed / files.length) * 100;
  }, [selectedEmployee, groupedByEmployee]);

  const hasHydratedFromProp = useRef(false);

  useEffect(() => {
    if (
      selectedEmployeeId &&
      groupedByEmployee[selectedEmployeeId] &&
      !hasHydratedFromProp.current
    ) {
      setSelectedEmployee(groupedByEmployee[selectedEmployeeId][0]);
      hasHydratedFromProp.current = true;
    }
  }, [selectedEmployeeId, groupedByEmployee]);

  useEffect(() => {
    if (!selectedEmployee && sortedEmployees.length > 0 && !selectedEmployeeId) {
      setSelectedEmployee(sortedEmployees[0]);
    }
  }, [sortedEmployees, selectedEmployee, selectedEmployeeId]);

  const renderFileSection = (label: string, files: any[], icon: JSX.Element) => {
    if (!files.length) return null;
    return (
      <div style={{ marginBottom: '2em' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <H3>
            {icon}
            {label}
          </H3>
          <H3 style={{ textAlign: 'right', flexShrink: 0 }}>Access Count</H3>
        </div>
        <div style={{ height: '1px', background: '#ccc', margin: '8px 0' }} />

        <List selection>
          {files.map((file, idx) => (
            <List.Item
              key={idx}
              style={{
                transition: 'background 0.15s',
                cursor: 'pointer',
                color: '#222',
              }}
              onClick={() => {
                if (onSelectFile) onSelectFile(file.filename);
              }}
              onMouseEnter={(e: { currentTarget: { style: { background: string } } }) =>
                (e.currentTarget.style.background = '#f5f7fa')
              }
              onMouseLeave={(e: { currentTarget: { style: { background: string } } }) =>
                (e.currentTarget.style.background = '')
              }
            >
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                  <StatusIcon status={file.access_count > 0 ? 5 : 1} size="large" />
                  {file.filename}
                </div>
                <div>{file.access_count}</div>
              </div>
            </List.Item>
          ))}
        </List>
      </div>
    );
  };

  const noFilesAvailable = !sortedEmployees.length;

  return (
    <PageSection width="100%" display="flex" alignItems="flex-start" gap="10px">
      <LeftColumn>
        <StyledCard>
          <FixedHeader>
            <Subtitle>Team Members</Subtitle>
            {isLoading ? (
              <Loader active inline="centered" />
            ) : noFilesAvailable ? (
              <PageSection padtop="12px" padbottom="10px">
                <Divider style={{ margin: '0px 0px 10px 0px' }} />
                <PageSection padtop="10px" padleft="12px" padright>
                  <p>No resources have been shared with your teams.</p>
                </PageSection>
              </PageSection>
            ) : (
              <table style={{ width: '100%', tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    {[
                      { key: 'employee_name', label: 'Member' },
                      { key: 'option_name', label: 'Team' },
                    ].map(col => {
                      const isActive = sortKey === col.key;
                      const icon = isActive ? (
                        sortDirection === 'asc' ? (
                          <Icon name="caret up" />
                        ) : (
                          <Icon name="caret down" />
                        )
                      ) : null;

                      return (
                        <th
                          key={col.key}
                          onClick={() => {
                            if (isActive)
                              setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
                            else setSortKey(col.key as any);
                          }}
                          style={{
                            cursor: 'pointer',
                            textAlign: 'left',
                            padding: '8px 4px',
                            verticalAlign: 'middle',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#333',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                          >
                            {col.label}
                            {icon}
                          </div>
                        </th>
                      );
                    })}
                  </tr>

                  <tr>
                    <td colSpan={2}>
                      <hr style={{ border: 0, height: '1px', background: '#ccc', margin: 0 }} />
                    </td>
                  </tr>
                </thead>
              </table>
            )}
          </FixedHeader>

          <ScrollableList>
            <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
              <tbody>
                {sortedEmployees.map(emp => (
                  <tr
                    key={emp.employee_id}
                    onClick={() => setSelectedEmployee(emp)}
                    style={{
                      background:
                        selectedEmployee?.employee_id === emp.employee_id
                          ? '#e6f0fa'
                          : 'transparent',
                      cursor: 'pointer',
                      transition: 'background 0.15s, box-shadow 0.15s',
                      boxShadow:
                        selectedEmployee?.employee_id === emp.employee_id
                          ? '0 2px 8px rgba(80,150,255,0.10)'
                          : undefined,
                      borderRadius:
                        selectedEmployee?.employee_id === emp.employee_id ? '6px' : undefined,
                      fontWeight:
                        selectedEmployee?.employee_id === emp.employee_id ? 500 : undefined,
                      color:
                        selectedEmployee?.employee_id === emp.employee_id ? '#2356a8' : undefined,
                    }}
                    onMouseEnter={e => {
                      if (selectedEmployee?.employee_id !== emp.employee_id)
                        e.currentTarget.style.background = '#f5f7fa';
                    }}
                    onMouseLeave={e => {
                      if (selectedEmployee?.employee_id !== emp.employee_id)
                        e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <td
                      colSpan={1}
                      style={{
                        padding: '6px',
                        borderRight: 'none',
                      }}
                    >
                      {emp.employee_name}
                    </td>
                    <td
                      colSpan={1}
                      style={{
                        padding: '6px',
                        borderLeft: 'none',
                      }}
                    >
                      {emp.option_name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollableList>
        </StyledCard>
      </LeftColumn>
      {!noFilesAvailable && (
        <RightColumn>
          <StyledCard>
            <PageSection padtop="10px" padleft padright padbottom>
              {selectedEmployee ? (
                <>
                  <ControlContainerRow padright="10px">
                    <ControlContainer justify="left" flex="3">
                      <PageSection display="flex" flexDirection="column">
                        <Subtitle>{selectedEmployee.employee_name}</Subtitle>
                        <Caption>Files shared with this member and their access history</Caption>
                      </PageSection>
                    </ControlContainer>
                    <ControlContainer justify="right">
                      <DoughnutGauge
                        complete={percentAccessed}
                        percent={true}
                        size="tiny"
                        max={100}
                        precision={0}
                        noWrap={true}
                      />
                    </ControlContainer>
                  </ControlContainerRow>
                  <PageSection padtop="30px">
                    {renderFileSection(
                      'PDFs',
                      categorizedFiles[FileTypes.PDF],
                      getFileTypeIcon(FileTypes.PDF) ?? <Icon name="file" />
                    )}
                    {renderFileSection(
                      'Videos',
                      categorizedFiles[FileTypes.MP4],
                      getFileTypeIcon(FileTypes.MP4) ?? <Icon name="file" />
                    )}
                    {renderFileSection(
                      'Links',
                      categorizedFiles[FileTypes.LINK],
                      getFileTypeIcon(FileTypes.LINK) ?? <Icon name="file" />
                    )}
                  </PageSection>
                </>
              ) : (
                <p>Select a member to view their resources</p>
              )}
            </PageSection>
          </StyledCard>
        </RightColumn>
      )}
    </PageSection>
  );
};

export default AnalyticsResourceMember;

const LeftColumn = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-right: 15px;
  @media (max-width: 768px) {
    flex: 1 1 100%;
  }
`;

const RightColumn = styled.div`
  flex: 3;
  display: flex;
  flex-direction: column;
  gap: 10px;
  @media (max-width: 768px) {
    flex: 1 1 100%;
  }
`;

const FixedHeader = styled.div`
  padding: 12px;
  flex-shrink: 0;
  background: white;
`;

const ScrollableList = styled.div`
  overflow-y: auto;
  padding: 0px 16px;
  flex-grow: 1;
  max-height: 60vh;
  position: relative;
  z-index: 0;
`;
